import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ProposalProvider } from "./contexts/ProposalContext";
import { ChatProvider } from "./contexts/ChatContext";
import { EditableDocumentProvider } from "./contexts/EditableDocumentContext";

// NEW: Updated page components
import LoginPage from "./pages/LoginPage";
import NewDashboard from "./pages/NewDashboard"; // Our new dashboard
import ProposalWorkspace from "./pages/ProposalWorkspace"; // Our new unified workspace
import ProposalChatPage from "./pages/ProposalChatPage";
import ProposalDevelopmentPage from "./pages/ProposalDevelopmentPage";
import RFPResearchPage from "./pages/RFPResearchPage";
import RFPAnalysisDetail from "./pages/RFPAnalysisDetail";

// LEGACY: Keep some existing pages for backward compatibility/admin
import ProposalDetail from "./pages/ProposalDetail"; // Keep for now
import ProposalsPage from "./pages/ProposalsPage"; // Keep for "View All" functionality
import ProposalFormPage from "./pages/ProposalFormPage"; // Keep for advanced editing
import ChatInterface from "./pages/ChatInterface"; // Keep for standalone chat sessions
import DocumentManagement from "./pages/DocumentManagement"; // Keep for legacy routes
import AdminPanel from "./pages/AdminPanel";
import DocumentBuilder from "./pages/DocumentBuilder";
import HolisticGenerationPage from "./pages/HolisticGenerationPage";
import LoadingScreen from "./components/common/LoadingScreen";
import ErrorBoundary from "./components/common/ErrorBoundary";

// Font Awesome setup (keep existing)
import { library } from "@fortawesome/fontawesome-svg-core";
import {
  faUser,
  faLock,
  faPlus,
  faUpload,
  faDownload,
  faTrash,
  faEdit,
  faSearch,
  faCog,
  faSignOutAlt,
  faHome,
  faRobot,
  faFile,
  faUsers,
  faChartBar,
  faExclamationTriangle,
  faCheck,
  faTimes,
  faSpinner,
  faComment,
  faComments,
  faPaperPlane,
  faBookmark,
  faEllipsisV,
  faChevronDown,
  faChevronRight,
  faChevronLeft,
  faEye,
  faEyeSlash,
  faCopy,
  faExternalLinkAlt,
  faBell,
  faQuestionCircle,
  faLightbulb,
  faRocket,
  faShield,
  faBars,
  faChevronUp,
  faRefresh,
  faFileAlt,
  faSignInAlt,
  faClock,
  faCalendar,
  faFilePdf,
  faFileWord,
  faFileExcel,
  faFilePowerpoint,
  faArrowLeft,
  faArrowRight, // Add this
  faMagic,
  faSyncAlt,
  faInfoCircle, // Add this
  // Document builder icons
  faCogs,
  faHistory,
  faDollarSign,
  faStar,
  faBuilding,
  faList,
  faArchive,
  faLink,
  faUnlink,
  faPaperclip,
  faCheckCircle,
  faPhone,
  faFlask,
  faSave,
  // New DocumentBuilder icons (only add ones not already declared)
  faBold,
  faItalic,
  faStrikethrough,
  faListUl,
  faListOl,
  faAlignLeft,
  faAlignCenter,
  faAlignRight,
  faAlignJustify,
  faUndo,
  faRedo,
  faCircle,
  faCircleHalfStroke,
  faCircleCheck,
  faCodeCompare,
  faChartLine,
  faTable,
  // RFP Research icons
  faCloudUploadAlt,
  faFileArchive,
  faTimesCircle,
  faTrophy,
  faCertificate,
  faInbox,
  faBox,
  faListAlt,
  faUserTie,
  faFileContract,
  faExclamationCircle,
  faImage,
  faHighlighter,
  faEraser,
} from "@fortawesome/free-solid-svg-icons";

library.add(
  faUser,
  faLock,
  faPlus,
  faUpload,
  faDownload,
  faTrash,
  faEdit,
  faSearch,
  faCog,
  faSignOutAlt,
  faHome,
  faRobot,
  faFile,
  faUsers,
  faChartBar,
  faExclamationTriangle,
  faCheck,
  faTimes,
  faSpinner,
  faComment,
  faComments,
  faPaperPlane,
  faBookmark,
  faEllipsisV,
  faChevronDown,
  faChevronRight,
  faChevronLeft,
  faEye,
  faEyeSlash,
  faCopy,
  faExternalLinkAlt,
  faBell,
  faQuestionCircle,
  faLightbulb,
  faRocket,
  faShield,
  faBars,
  faChevronUp,
  faRefresh,
  faFileAlt,
  faSignInAlt,
  faClock,
  faCalendar,
  faFilePdf,
  faFileWord,
  faFileExcel,
  faFilePowerpoint,
  faArrowLeft,
  faArrowRight,
  faMagic,
  faSyncAlt,
  faInfoCircle,
  // Document builder icons
  faCogs,
  faHistory,
  faDollarSign,
  faStar,
  faBuilding,
  faList,
  faArchive,
  faLink,
  faUnlink,
  faPaperclip,
  faCheckCircle,
  faPhone,
  faFlask,
  faSave,
  // New DocumentBuilder icons (only add ones not already declared)
  faBold,
  faItalic,
  faStrikethrough,
  faListUl,
  faListOl,
  faAlignLeft,
  faAlignCenter,
  faAlignRight,
  faAlignJustify,
  faUndo,
  faRedo,
  faCircle,
  faCircleHalfStroke,
  faCircleCheck,
  faCodeCompare,
  faChartLine,
  faTable,
  // RFP Research icons
  faCloudUploadAlt,
  faFileArchive,
  faTimesCircle,
  faTrophy,
  faCertificate,
  faInbox,
  faBox,
  faListAlt,
  faUserTie,
  faFileContract,
  faExclamationCircle,
  faImage,
  faHighlighter,
  faEraser
);

// Protected Route Component
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

// Admin Route Component
function AdminRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user || user.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function AppRoutes() {
  const { user } = useAuth();

  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={user ? <Navigate to="/dashboard" replace /> : <LoginPage />}
      />

      {/* NEW: Main Application Flow */}

      {/* Dashboard - General chat + proposal selection */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <ProposalProvider>
              <ChatProvider>
                <NewDashboard />
              </ChatProvider>
            </ProposalProvider>
          </ProtectedRoute>
        }
      />

      {/* ADD THIS NEW ROUTE: Proposal Chat Selection Page */}
      <Route
        path="/proposal-chat"
        element={
          <ProtectedRoute>
            <ProposalProvider>
              <ProposalChatPage />
            </ProposalProvider>
          </ProtectedRoute>
        }
      />

      {/* Proposal Development Selection Page */}
      <Route
        path="/proposal-development"
        element={
          <ProtectedRoute>
            <ProposalProvider>
              <ProposalDevelopmentPage />
            </ProposalProvider>
          </ProtectedRoute>
        }
      />

      {/* RFP Research Routes */}
      <Route
        path="/rfp-research"
        element={
          <ProtectedRoute>
            <RFPResearchPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/rfp-research/:id"
        element={
          <ProtectedRoute>
            <RFPAnalysisDetail />
          </ProtectedRoute>
        }
      />

      {/* NEW: Unified Proposal Workspace */}
      <Route
        path="/workspace/:proposalId"
        element={
          <ProtectedRoute>
            <ProposalProvider>
              <ChatProvider>
                <EditableDocumentProvider>
                  <ProposalWorkspace />
                </EditableDocumentProvider>
              </ChatProvider>
            </ProposalProvider>
          </ProtectedRoute>
        }
      />

      {/* Document Builder - Document list view */}
      <Route
        path="/workspace/:proposalId/documents"
        element={
          <ProtectedRoute>
            <ProposalProvider>
              <ChatProvider>
                <EditableDocumentProvider>
                  <DocumentBuilder />
                </EditableDocumentProvider>
              </ChatProvider>
            </ProposalProvider>
          </ProtectedRoute>
        }
      />

      {/* Holistic Generation Page */}
      <Route
        path="/proposals/:proposalId/generate"
        element={
          <ProtectedRoute>
            <ProposalProvider>
              <HolisticGenerationPage />
            </ProposalProvider>
          </ProtectedRoute>
        }
      />

      {/* Document Builder - Specific document editor */}
      <Route
        path="/workspace/:proposalId/document/:documentId"
        element={
          <ProtectedRoute>
            <ProposalProvider>
              <ChatProvider>
                <EditableDocumentProvider>
                  <DocumentBuilder />
                </EditableDocumentProvider>
              </ChatProvider>
            </ProposalProvider>
          </ProtectedRoute>
        }
      />

      {/* LEGACY: Keep existing routes for backward compatibility and specific use cases */}

      {/* Standalone Chat Interface - for general chats and deep linking */}
      <Route
        path="/chat"
        element={
          <ProtectedRoute>
            <ProposalProvider>
              <ChatProvider>
                <ChatInterface />
              </ChatProvider>
            </ProposalProvider>
          </ProtectedRoute>
        }
      />

      <Route
        path="/chat/:sessionId"
        element={
          <ProtectedRoute>
            <ProposalProvider>
              <ChatProvider>
                <ChatInterface />
              </ChatProvider>
            </ProposalProvider>
          </ProtectedRoute>
        }
      />

      {/* Proposal Management - "View All" functionality */}
      <Route
        path="/proposals"
        element={
          <ProtectedRoute>
            <ProposalProvider>
              <ProposalsPage />
            </ProposalProvider>
          </ProtectedRoute>
        }
      />

      {/* Advanced Proposal Creation/Editing */}
      <Route
        path="/proposals/new"
        element={
          <ProtectedRoute>
            <ProposalProvider>
              <ProposalFormPage />
            </ProposalProvider>
          </ProtectedRoute>
        }
      />

      <Route
        path="/proposals/edit/:id"
        element={
          <ProtectedRoute>
            <ProposalProvider>
              <ProposalFormPage />
            </ProposalProvider>
          </ProtectedRoute>
        }
      />

      {/* Legacy Proposal Detail - redirect to workspace */}
      <Route
        path="/proposal/:id"
        element={
          <ProtectedRoute>
            <Navigate to="/workspace/:id" replace />
          </ProtectedRoute>
        }
      />

      <Route
        path="/proposal-details/:id"
        element={
          <ProtectedRoute>
            <ProposalProvider>
              <ChatProvider>
                <ProposalDetail />
              </ChatProvider>
            </ProposalProvider>
          </ProtectedRoute>
        }
      />

      {/* Legacy Document Builder routes - redirect to workspace */}
      <Route
        path="/proposals/:proposalId/documents"
        element={
          <ProtectedRoute>
            <Navigate to="/workspace/:proposalId" replace />
          </ProtectedRoute>
        }
      />

      <Route
        path="/proposals/:proposalId/documents/:documentId"
        element={
          <ProtectedRoute>
            <Navigate
              to="/workspace/:proposalId/document/:documentId"
              replace
            />
          </ProtectedRoute>
        }
      />

      <Route
        path="/document-builder"
        element={
          <ProtectedRoute>
            <Navigate to="/dashboard" replace />
          </ProtectedRoute>
        }
      />

      {/* Legacy Document Management - keep for now but could be deprecated */}
      <Route
        path="/documents"
        element={
          <ProtectedRoute>
            <ProposalProvider>
              <DocumentManagement />
            </ProposalProvider>
          </ProtectedRoute>
        }
      />

      <Route
        path="/documents/:proposalId"
        element={
          <ProtectedRoute>
            <ProposalProvider>
              <DocumentManagement />
            </ProposalProvider>
          </ProtectedRoute>
        }
      />

      {/* Admin Routes */}
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AdminPanel />
          </AdminRoute>
        }
      />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // App initialization logic
    const initializeApp = async () => {
      try {
        // Check if we have a stored token and validate it
        // This will be handled by AuthContext
        await new Promise((resolve) => setTimeout(resolve, 100)); // Slight delay for smooth loading
        setIsInitialized(true);
      } catch (error) {
        console.error("App initialization error:", error);
        setIsInitialized(true); // Still show the app even if there's an error
      }
    };

    initializeApp();
  }, []);

  if (!isInitialized) {
    return <LoadingScreen />;
  }

  return (
    <ErrorBoundary>
      <Router>
        <AuthProvider>
          <div className="App min-h-screen bg-gray-50">
            <AppRoutes />
          </div>
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
